import { adaptProjection } from 'adaptors/adaptAllocations';
import { getfromDataObject, validateNumber } from 'utils/formatting';

/** * Define the shape of the options parameter
 */
interface AdaptorOptions {
    clientId?: string | number;
    t?: (key: string) => string;
    getFormattedDate?: (date: any) => string;
    getFormattedCurrency?: (value: any, options?: Intl.NumberFormatOptions) => string;
}

/** * Defining the main function with TypeScript
 */
export const adaptPortfolioDetails = (data: any, options: AdaptorOptions = {}) => {
    const getFormattedDate = options?.getFormattedDate || ((d: any) => d);
    const getFormattedCurrency = options?.getFormattedCurrency || ((d: any) => d);

    let currency: string | undefined;
    let positions: any[] = [];
    let currencyId: string | number | undefined;
    let customAttributes: Record<string, any> = {};
    const productID = getfromDataObject(data, 'Product.Id');

    const formatCurrency = (value: any) => getFormattedCurrency(value, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    });

    try {
        if (data?.CustomAttributes) {
            const attributes = JSON.parse(data.CustomAttributes);

            customAttributes = {
                ...attributes,
                YearlyPayments: formatCurrency(attributes?.YearlyPayments),
                AbsoluteReturnPostGoLive: formatCurrency(attributes?.AbsoluteReturnPostGoLive),
                AbsoluteReturnPreGoLive: formatCurrency(attributes?.AbsoluteReturnPreGoLive),
                AbsoluteReturn: formatCurrency(attributes?.AbsoluteReturn || 0),
                CreateDate: getFormattedDate(attributes?.CreateDate),
                ReturnStartDate: getFormattedDate(attributes?.ReturnStartDate),
            };
        }
    } catch (e) {
        // Fallback or silent fail as per original logic
        console.error('Failed to parse CustomAttributes', e);
    }

    if (data?.Currency) {
        currency = data.Currency.CurrencyCode;
        currencyId = data.Currency.Id;
    } else if (data?.BaseCurrency) {
        currency = data.BaseCurrency.CurrencyCode;
        // Logic fix: Original code accessed data.Currency.Id here,
        // which might be undefined if BaseCurrency is used.
        currencyId = data.BaseCurrency.Id || data.Currency?.Id;
    }

    if (data?.Positions) {
        positions = data.Positions;
    } else if (data?.ConsolidatedPositions) {
        positions = data.ConsolidatedPositions;
    }

    return {
        id: data?.Id,
        backTitle: getfromDataObject(data, 'Name'),
        title: getfromDataObject(data, 'Name'),
        currency,
        currencyId,
        contactGroupId: data?.ContactGroupId,
        customAttributes,
        uid: data?.UId,
        benchmarkId: data?.BenchmarkId,
        createDate: getFormattedDate(data?.CreateDate),
        portfolioValue: data?.CurrentValue,
        investedAmount: data?.InvestedAmount,
        securitiesValue: data?.SecuritiesValue,
        modelPortfolioId: data?.ModelPortfolioId,
        esgPortfolioId: customAttributes?.esg,
        overview: {
            portfolioValue: data?.CurrentValue,
            currency,
            strategy: getfromDataObject(data, 'RiskCategory.Name'),
            product: getfromDataObject(data, 'Product.Name'),
            performance: validateNumber(data?.CurrentPerformance) * 100,
            sharpRatio: data?.SharpeRatio,
            expectedReturn: validateNumber(data?.CalculatedExpectedReturn) * 100,
            expectedRisk: validateNumber(data?.CalculatedRisk) * 100,
            firstInvestment: getFormattedDate(data?.FirstInvestmentDate),
            modelPortfolio: data?.ModelPortfolioName,
        },
        riskCategoryId: getfromDataObject(data, 'RiskCategory.Id'),
        constraints: data?.OptimizationConstraints || {},
        investmentAllocations: (data?.Positions || []).map((item: any) => ({
            InstrumentId: item.Security?.Id,
            Allocation: Number(item.Allocation),
        })),
        productId: productID,
        projection: adaptProjection({
            data: positions,
            CurrencyId: currencyId,
            ProjectionYears: 5,
            InitialInvestmentAmount: data?.CurrentValue,
        }),
        riskReturn: [
            +(validateNumber(data?.CalculatedRisk) * 100),
            +(validateNumber(data?.CalculatedExpectedReturn) * 100),
        ] as [number, number],
        riskReturnMatrix: {
            x: [
                validateNumber(data?.RiskCategory?.Min) * 100,
                validateNumber(data?.RiskCategory?.Max) * 100,
            ] as [number, number],
        },
        HistoricalPerformance: data?.HistoricalPerformance,
        isBreached: data?.IsBreached,
        portfolioStatusId: data?.PortfolioStatusId,
        hasPendingOrders: data?.HasPendingOrders,
    };
};
